// Mock data for Admin Dashboard demo
// This augments real data from Redux with demo-specific values

import {
  RegionSummary,
  StateOverviewData,
  SiteOverviewData,
  ProgramOverviewData,
  ComplianceData,
  AIConciergeStats,
  CityDistribution,
  TopService,
  ProgramTag,
  ComplianceStatus,
  TimeframeOption,
} from './adminDashboardTypes'
import { Worksite } from 'redux/employer/employerTypes'

// Timeframe-based At a Glance stats
// Current values (eligible, enrolled, participation) stay the same - only comparison period changes
// Cumulative values (tax savings, carbon) change based on timeframe period
export const mockAtAGlanceByTimeframe: Record<
  TimeframeOption,
  {
    eligibleCommuters: number
    enrolledCommuters: number
    participationRate: number
    taxSavings: number
    carbonReduction: number
    trends: {
      eligible: { value: number; label: string }
      enrolled: { value: number; label: string }
      participation: { value: number; label: string }
      taxSavings: { value: string; label: string }
      carbon: { value: string; label: string }
    }
  }
> = {
  '7d': {
    eligibleCommuters: 8000,
    enrolledCommuters: 6680,
    participationRate: 84,
    taxSavings: 119200,
    carbonReduction: 816,
    trends: {
      eligible: { value: 0.2, label: '+24 vs last week' },
      enrolled: { value: 0.8, label: '+56 vs last week' },
      participation: { value: 0.5, label: '+0.5% vs last week' },
      taxSavings: { value: '+$8k', label: '+$8k vs last week' },
      carbon: { value: '+72kg', label: '+72kg vs last week' },
    },
  },
  '30d': {
    eligibleCommuters: 8000,
    enrolledCommuters: 6680,
    participationRate: 84,
    taxSavings: 477600,
    carbonReduction: 3264,
    trends: {
      eligible: { value: 1.2, label: '+96 vs last month' },
      enrolled: { value: 2.8, label: '+184 vs last month' },
      participation: { value: 1.5, label: '+1.5% vs last month' },
      taxSavings: { value: '+$32k', label: '+$32k vs last month' },
      carbon: { value: '+248kg', label: '+248kg vs last month' },
    },
  },
  '90d': {
    eligibleCommuters: 8000,
    enrolledCommuters: 6680,
    participationRate: 84,
    taxSavings: 1432000,
    carbonReduction: 9776,
    trends: {
      eligible: { value: 3.8, label: '+296 vs last quarter' },
      enrolled: { value: 8.2, label: '+504 vs last quarter' },
      participation: { value: 4.1, label: '+4.1% vs last quarter' },
      taxSavings: { value: '+$120k', label: '+$120k vs last quarter' },
      carbon: { value: '+728kg', label: '+728kg vs last quarter' },
    },
  },
}

// Timeframe-based top services
export const mockTopServicesByTimeframe: Record<TimeframeOption, TopService[]> =
  {
    '7d': [
      { name: 'MARTA', usage: 2496 },
      { name: 'Metro Transit', usage: 1544 },
      { name: 'UTA', usage: 1024 },
      { name: 'MTA New York City Transit', usage: 896 },
      { name: 'LA Metro', usage: 600 },
    ],
    '30d': [
      { name: 'MARTA', usage: 9976 },
      { name: 'Metro Transit', usage: 6184 },
      { name: 'UTA', usage: 4088 },
      { name: 'MTA New York City Transit', usage: 3592 },
      { name: 'LA Metro', usage: 2392 },
    ],
    '90d': [
      { name: 'MARTA', usage: 29928 },
      { name: 'Metro Transit', usage: 18552 },
      { name: 'UTA', usage: 12272 },
      { name: 'MTA New York City Transit', usage: 10776 },
      { name: 'LA Metro', usage: 7184 },
    ],
  }

// Timeframe-based AI Concierge stats
export const mockAIConciergeByTimeframe: Record<
  TimeframeOption,
  AIConciergeStats
> = {
  '7d': {
    questionsHandled: 7616,
    questionsHandledTrend: { value: 12, direction: 'up', label: '12%' },
    autoResolved: 632,
    autoResolvedTrend: { value: 3, direction: 'up', label: '3%' },
    avgResponseTime: 4.8,
    avgResponseTimeTrend: { value: 5, direction: 'up', label: '5%' },
    topTopic: 'Transit pass enrollment',
    topTopicCount: 1584,
  },
  '30d': {
    questionsHandled: 32480,
    questionsHandledTrend: { value: 18, direction: 'up', label: '18%' },
    autoResolved: 656,
    autoResolvedTrend: { value: 5, direction: 'up', label: '5%' },
    avgResponseTime: 4.3,
    avgResponseTimeTrend: { value: 10, direction: 'up', label: '10%' },
    topTopic: 'Transit pass enrollment',
    topTopicCount: 6776,
  },
  '90d': {
    questionsHandled: 97440,
    questionsHandledTrend: { value: 24, direction: 'up', label: '24%' },
    autoResolved: 680,
    autoResolvedTrend: { value: 8, direction: 'up', label: '8%' },
    avgResponseTime: 3.9,
    avgResponseTimeTrend: { value: 15, direction: 'up', label: '15%' },
    topTopic: 'Transit pass enrollment',
    topTopicCount: 20328,
  },
}

// Helper to get city abbreviation
const getCityAbbreviation = (city: string): string => {
  const cityMap: Record<string, string> = {
    Atlanta: 'ATL',
    Minneapolis: 'MSP',
    'St Paul': 'MSP',
    'Salt Lake City': 'SLC',
    Detroit: 'DTW',
    Jamaica: 'JFK',
    Flushing: 'LGA',
    'Los Angeles': 'LAX',
    Seattle: 'SEA',
    Boston: 'BOS',
  }
  // Check if city starts with any key
  for (const [fullName, abbr] of Object.entries(cityMap)) {
    if (city?.toLowerCase().includes(fullName.toLowerCase())) {
      return abbr
    }
  }
  // Return first 3 chars uppercase if no match
  return city?.slice(0, 3).toUpperCase() || 'N/A'
}

// Mock enrollment percentages and programs per site (for demo)
const mockSiteEnhancements: Record<
  string,
  {
    enrollmentPercent: number
    programs: ProgramTag[]
    complianceStatus: ComplianceStatus
    syncTime: string | null
  }
> = {
  default: {
    enrollmentPercent: 84,
    programs: ['Transit', 'Parking', 'Micromobility'],
    complianceStatus: 'Compliant',
    syncTime: '2 hours ago',
  },
}

// Hub -> home state. The overview grid groups by state so New York reads as one
// place with two airports rather than as two unrelated cards.
const STATE_BY_HUB: Record<string, { state: string; abbreviation: string }> = {
  ATL: { state: 'Georgia', abbreviation: 'GA' },
  MSP: { state: 'Minnesota', abbreviation: 'MN' },
  SLC: { state: 'Utah', abbreviation: 'UT' },
  DTW: { state: 'Michigan', abbreviation: 'MI' },
  JFK: { state: 'New York', abbreviation: 'NY' },
  LGA: { state: 'New York', abbreviation: 'NY' },
  LAX: { state: 'California', abbreviation: 'CA' },
  SEA: { state: 'Washington', abbreviation: 'WA' },
  BOS: { state: 'Massachusetts', abbreviation: 'MA' },
}

// Program tags render in a fixed order so cards stay comparable at a glance.
const TAG_ORDER: ProgramTag[] = [
  'Transit',
  'Parking',
  'Rideshare',
  'Micromobility',
  'Subsidy',
]

/**
 * Group site cards into one card per state.
 *
 * Headcount sums, enrollment is weighted by headcount (so a small hub cannot
 * drag a large one), program tags are the union across the state's hubs, and
 * compliance becomes a count because a state can be mixed.
 */
export const groupSitesByState = (
  sites: SiteOverviewData[]
): StateOverviewData[] => {
  const byState = new Map<string, StateOverviewData>()

  sites.forEach((site) => {
    const home = STATE_BY_HUB[site.abbreviation]
    if (!home) return

    const region: RegionSummary = {
      id: site.id,
      code: site.abbreviation,
      name: site.name,
      employeeCount: site.employeeCount,
      complianceStatus: site.complianceStatus,
    }

    const existing = byState.get(home.state)
    if (!existing) {
      byState.set(home.state, {
        id: home.abbreviation,
        state: home.state,
        abbreviation: home.abbreviation,
        employeeCount: site.employeeCount,
        enrollmentPercent: site.enrollmentPercent,
        programs: [...site.programs],
        regions: [region],
        compliantRegions: site.complianceStatus === 'Compliant' ? 1 : 0,
        syncTime: site.syncTime,
        isActive: site.isActive,
      })
      return
    }

    const totalEmployees = existing.employeeCount + site.employeeCount
    const weighted =
      existing.enrollmentPercent * existing.employeeCount +
      site.enrollmentPercent * site.employeeCount

    existing.employeeCount = totalEmployees
    existing.enrollmentPercent = totalEmployees
      ? Math.round(weighted / totalEmployees)
      : 0
    existing.regions.push(region)
    existing.compliantRegions += site.complianceStatus === 'Compliant' ? 1 : 0
    existing.isActive = existing.isActive || site.isActive
    site.programs.forEach((tag) => {
      if (!existing.programs.includes(tag)) existing.programs.push(tag)
    })
  })

  return Array.from(byState.values())
    .map((entry) => ({
      ...entry,
      programs: TAG_ORDER.filter((tag) => entry.programs.includes(tag)),
      regions: [...entry.regions].sort(
        (a, b) => b.employeeCount - a.employeeCount
      ),
    }))
    .sort((a, b) => b.employeeCount - a.employeeCount)
}

// Transform worksites to site overview data
export const transformWorksitesToSiteData = (
  worksites: Worksite[] | null
): SiteOverviewData[] => {
  // Always return mock data for demo
  return mockSites
}

// Transform worksites to geographic distribution
export const transformWorksitesToGeoData = (
  worksites: Worksite[] | null
): CityDistribution[] => {
  // Always return mock data for demo
  return mockCityDistribution
}

// Fallback mock sites when no real data
export const mockSites: SiteOverviewData[] = [
  {
    id: '1',
    name: 'Atlanta',
    abbreviation: 'ATL',
    employeeCount: 3256,
    enrollmentPercent: 86,
    programs: ['Transit', 'Parking', 'Micromobility'],
    complianceStatus: 'Compliant',
    syncTime: '2 hours ago',
    isActive: true,
  },
  {
    id: '2',
    name: 'Minneapolis',
    abbreviation: 'MSP',
    employeeCount: 1312,
    enrollmentPercent: 81,
    programs: ['Transit', 'Parking'],
    complianceStatus: 'Compliant',
    syncTime: '3 hours ago',
    isActive: true,
  },
  {
    id: '3',
    name: 'Salt Lake City',
    abbreviation: 'SLC',
    employeeCount: 792,
    enrollmentPercent: 78,
    programs: ['Transit', 'Parking'],
    complianceStatus: 'Compliant',
    syncTime: '1 hour ago',
    isActive: true,
  },
  {
    id: '4',
    name: 'Detroit',
    abbreviation: 'DTW',
    employeeCount: 536,
    enrollmentPercent: 64,
    programs: ['Transit', 'Parking'],
    complianceStatus: 'Action Needed',
    syncTime: '5 hours ago',
    isActive: true,
  },
  {
    id: '5',
    name: 'New York JFK',
    abbreviation: 'JFK',
    employeeCount: 520,
    enrollmentPercent: 88,
    programs: ['Transit', 'Parking', 'Rideshare', 'Subsidy'],
    complianceStatus: 'Compliant',
    syncTime: '2 hours ago',
    isActive: true,
  },
  {
    id: '6',
    name: 'Los Angeles',
    abbreviation: 'LAX',
    employeeCount: 520,
    enrollmentPercent: 83,
    programs: ['Transit', 'Parking', 'Rideshare'],
    complianceStatus: 'Compliant',
    syncTime: '4 hours ago',
    isActive: true,
  },
  {
    id: '7',
    name: 'Seattle',
    abbreviation: 'SEA',
    employeeCount: 408,
    enrollmentPercent: 90,
    programs: ['Transit', 'Parking', 'Micromobility'],
    complianceStatus: 'Compliant',
    syncTime: '1 hour ago',
    isActive: true,
  },
  {
    id: '8',
    name: 'Boston',
    abbreviation: 'BOS',
    employeeCount: 328,
    enrollmentPercent: 79,
    programs: ['Transit', 'Parking'],
    complianceStatus: 'Compliant',
    syncTime: '6 hours ago',
    isActive: true,
  },
  {
    id: '9',
    name: 'New York LGA',
    abbreviation: 'LGA',
    employeeCount: 328,
    enrollmentPercent: 72,
    programs: ['Transit', 'Parking', 'Rideshare', 'Subsidy'],
    complianceStatus: 'Action Needed',
    syncTime: '3 hours ago',
    isActive: true,
  },
]

// Mock programs data
export const mockPrograms: ProgramOverviewData[] = [
  {
    id: '1',
    name: 'Transit Benefit',
    icon: 'dollar',
    totalEnrolled: 4096,
    sitesActive: 9,
    totalSites: 9,
    siteBreakdown: [
      { siteName: 'Atlanta', count: 1664 },
      { siteName: 'Minneapolis', count: 672 },
      { siteName: 'Salt Lake City', count: 408 },
      { siteName: 'New York JFK', count: 264 },
    ],
  },
  {
    id: '2',
    name: 'Employee Parking',
    icon: 'car',
    totalEnrolled: 1944,
    sitesActive: 6,
    totalSites: 9,
    siteBreakdown: [
      { siteName: 'Atlanta', count: 784 },
      { siteName: 'Minneapolis', count: 320 },
      { siteName: 'Detroit', count: 272 },
    ],
  },
  {
    id: '3',
    name: 'Crew Rideshare',
    icon: 'car',
    totalEnrolled: 1392,
    sitesActive: 5,
    totalSites: 9,
    siteBreakdown: [
      { siteName: 'Atlanta', count: 568 },
      { siteName: 'New York JFK', count: 192 },
      { siteName: 'Los Angeles', count: 176 },
    ],
  },
  {
    id: '4',
    name: 'Guaranteed Ride Home',
    icon: 'bus',
    totalEnrolled: 968,
    sitesActive: 9,
    totalSites: 9,
    siteBreakdown: [
      { siteName: 'Atlanta', count: 392 },
      { siteName: 'Minneapolis', count: 160 },
      { siteName: 'Seattle', count: 104 },
    ],
  },
  {
    id: '5',
    name: 'Micromobility',
    icon: 'bike',
    totalEnrolled: 544,
    sitesActive: 2,
    totalSites: 9,
    siteBreakdown: [
      { siteName: 'Atlanta', count: 456 },
      { siteName: 'Seattle', count: 88 },
    ],
  },
]

// Mock geographic distribution fallback
export const mockCityDistribution: CityDistribution[] = [
  { city: 'Atlanta', abbreviation: 'ATL', count: 3256 },
  { city: 'Minneapolis', abbreviation: 'MSP', count: 1312 },
  { city: 'Salt Lake City', abbreviation: 'SLC', count: 792 },
  { city: 'Detroit', abbreviation: 'DTW', count: 536 },
  { city: 'New York JFK', abbreviation: 'JFK', count: 520 },
  { city: 'Los Angeles', abbreviation: 'LAX', count: 520 },
  { city: 'Seattle', abbreviation: 'SEA', count: 408 },
  { city: 'Boston', abbreviation: 'BOS', count: 328 },
  { city: 'New York LGA', abbreviation: 'LGA', count: 328 },
]

// Mock compliance data
export const mockCompliance: ComplianceData = {
  compliantSites: 13,
  totalSites: 15,
  alerts: [
    {
      siteName: 'LGA - Airport Operations',
      message: 'NYC pre-tax transit benefit filing outstanding',
      dueInDays: 21,
    },
    {
      siteName: 'DTW - Airport Operations',
      message: 'Site setup pending - commuter benefits not yet configured',
      dueInDays: null,
    },
  ],
}

// Mock AI Concierge stats
export const mockAIConcierge: AIConciergeStats = {
  questionsHandled: 32480,
  questionsHandledTrend: {
    value: 18,
    direction: 'up',
    label: '18%',
  },
  autoResolved: 656,
  autoResolvedTrend: {
    value: 5,
    direction: 'up',
    label: '5%',
  },
  avgResponseTime: 4.3,
  avgResponseTimeTrend: {
    value: 10,
    direction: 'up',
    label: '10%',
  },
  topTopic: 'Transit pass enrollment',
  topTopicCount: 6776,
}

// Mock top services (fallback if no real data)
export const mockTopServices: TopService[] = [
  { name: 'MARTA', usage: 9976 },
  { name: 'Metro Transit', usage: 6184 },
  { name: 'UTA', usage: 4088 },
  { name: 'MTA New York City Transit', usage: 3592 },
  { name: 'LA Metro', usage: 2392 },
]
